import { nanoid } from 'nanoid'
import { useProjectStore } from './use-project-store'
import { create } from 'zustand'
import { immer } from 'zustand/middleware/immer'
import { useProjectRestore } from './use-project-restore'
import { useTranslations } from 'next-intl'

const MAX_RETRIES = 3

// 需要使用 store 才行
type IAiCodingRetryState = {
  retryIng: boolean
  retryCount: number
  retryQueue: IAiCodingRetryType[]
  getRetryCount: () => number
  getRetryQueue: () => IAiCodingRetryType[]
  setRetryIng: (retryIng: boolean) => void
  setRetryCount: (retryCount: number) => void
  clearRetry: () => void
  addRetryQueue: (retryType: IAiCodingRetryType) => void
}

// 目前只有两种类型的重试
type IAiCodingRetryType = 'Failed to response' | 'Failed to load'

// 只有 ai-coding 接口的调用，才会触发该 store 的启用
export const useAiCodingRetryStore = create<IAiCodingRetryState>()(
  immer((set, get) => ({
    retryIng: false,
    retryCount: 0,
    retryQueue: [],
    getRetryCount: () => get().retryCount,
    getRetryQueue: () => get().retryQueue,
    setRetryIng: (retryIng: boolean) => set({ retryIng }),
    setRetryCount: (retryCount: number) => set({ retryCount }),
    clearRetry: () => set({ retryIng: false, retryCount: 0, retryQueue: [] }),
    addRetryQueue: (retryType: IAiCodingRetryType) => {
      set((state) => ({ retryQueue: [...state.retryQueue, retryType] }))
    },
  })),
)

/**
 * 重试逻辑梳理，举个例子：
    1. 发送第一条信息 message1: change color 
    情况 1：触发 failed to load 错误，触发重试
    情况 2：触发 failed to response 错误，触发重试
    情况 3：脱离 ai-coding 流程，无重试逻辑，触发 js runtime 错误，用户自己点 fix issue

    2. （重试第一次）发送第二条信息 message2:  fix issue 携带错误 a is undefined
    情况 1：触发 failed to load 错误，触发重试
    情况 2：触发 failed to response 错误，触发重试
    情况 3：脱离 ai-coding 无重试逻辑，触发 js runtime 错误，用户自己点 fix issue

    3. （重试第二次）发送第三条信息 message3:  fix issue 携带错误变成 b is undefined ------（注意每次重试的错误原因可能是不同的，并且可能一会 failed to load 一会 failed to response）
    情况 1：触发 failed to load 错误，触发回滚
    情况 2：触发 failed to response 错误，可能命中触发回滚的逻辑、也可能只显示 retry 按钮（当每次重试都是因为 failed to response 错误时，会显示 retry 按钮）
    情况 3：脱离 ai-coding 无重试逻辑，触发 js runtime 错误，用户自己点 fix issue

    触发回滚逻辑:
    根据 currentTreeIndex - failedToLoadRetryCount 来判断，得到目标 treeIndex，调用 restore
 */
export function useAiCodingRetry() {
  const { addMessage, setAiStage, getIsOwner, getFileSystemTreeIndex } =
    useProjectStore()

  const {
    getRetryCount,
    setRetryIng,
    setRetryCount,
    clearRetry,
    getRetryQueue,
    addRetryQueue,
  } = useAiCodingRetryStore()

  const { restore } = useProjectRestore()

  const t = useTranslations('ai-chat')

  // 如果 retryType 是 'Failed to fix issue'，需要传入 retryReason
  const startRetryAiCoding = async (options: {
    retryType: IAiCodingRetryType
    retryAiCoding: () => Promise<any>
    retryReason?: string
    onRetryEnd?: () => void
  }) => {
    // 非项目 owner 无法触发重试逻辑
    if (!getIsOwner()) return

    const {
      retryReason,
      retryType,
      retryAiCoding,
      onRetryEnd = () => {},
    } = options || {}

    const count = getRetryCount() + 1
    setRetryCount(count)
    setRetryIng(true)

    if (count >= MAX_RETRIES) {
      // 重试完成了，才会进入这个分支
      // 计算 'Failed to response' | 'Failed to load' 总发生了几次
      const retryQueue = getRetryQueue()
      const failedToResponseCount = retryQueue.filter(
        (retryType) => retryType === 'Failed to response',
      ).length
      const failedToLoadCount = retryQueue.filter(
        (retryType) => retryType === 'Failed to load',
      ).length

      console.table({
        'Retry total count': retryQueue.length,
        'Failed to response count': failedToResponseCount,
        'Failed to load count': failedToLoadCount,
      })

      if (failedToResponseCount === MAX_RETRIES - 1) {
        // 重试原因全是为 failed to response 错误，那认为是网络问题，需要你自己来点 retry 了
        addMessage({
          id: nanoid(),
          role: 'system',
          type: 'retry',
        })
      } else if (
        failedToLoadCount === MAX_RETRIES - 1 ||
        failedToLoadCount > 0
      ) {
        // 情况 1：重试原因全是为 failed to load 错误
        // 情况 2：存在一会 failed to load 一会 failed to response 的错误
        // 只要其中有一个 retry 是因为 failed to load 错误，那可以触发回滚了
        // failedToLoadCount 最大也就等于 MAX_RETRIES - 1，即 2
        const rollbackIndex = getFileSystemTreeIndex() - (failedToLoadCount + 1)
        if (rollbackIndex >= 1) {
          addMessage({
            id: nanoid(),
            role: 'assistant',
            content:
              'Automatically rollback to previous version. I tried your request but ran into some challenges and couldn’t finish it quickly. Could you provide a simpler request? I’ll give it another try.',
          })

          restore(rollbackIndex)
        }

        // 回滚 + 重置为初始状态
        clearRetry()
        setAiStage('idle')
        onRetryEnd()

        return
      }
    }

    addRetryQueue(retryType)

    if (retryType === 'Failed to load') {
      // failed to load 错误，有错误原因，需要喂给 AI
      const messageId = nanoid()
      addMessage({
        id: messageId,
        role: 'user',
        content: t('still_improving'),
        contentForAi: `Fixing issue, error message: ${retryReason}`,
      })
    } else if (retryType === 'Failed to response') {
      // fail to response 纯粹重试，啥都不用干，不需要添加 message
    }

    await retryAiCoding()
  }

  const stopRetryAiCoding = () => {
    clearRetry()
  }

  return {
    startRetryAiCoding,
    stopRetryAiCoding,
  }
}
